import { Box, HStack, Stack, Table } from "@chakra-ui/react";
import { MaterialTypeEnum } from "@ns/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useFormContext } from "react-hook-form";
import { FaTrashAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

import { api } from "../api/custom-api.ts";
import ControlledForm from "../components/form/controlled-form.tsx";
import ControlledInput from "../components/form/controlled-input.tsx";
import Loader from "../components/loader.tsx";
import { CustomPagination } from "../components/ui/custom-pagination.tsx";
import { SearchSchema, searchSchema } from "../schemas/search.schema.ts";
import { materialTypeMapper } from "../utils/mappers/material-type.mapper.ts";
import { plateTypeMapper } from "../utils/mappers/plate-type.mapper.ts";
import {
  DialogActionTrigger,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog.tsx";
import { Button } from "../components/ui/button.tsx";
import { usePagination } from "../hooks";

const Codebook = () => {
  const { watch } = useFormContext<SearchSchema>();

  const search = watch("search") || "";

  const pageSize = 10;

  const {
    pageIndex,
    setPage,
    nextPage: onNextPage,
    previousPage: onPreviousPage,
    canPreviousPage,
  } = usePagination({
    pageType: "codebook",
    search,
    pageSize,
  });

  const navigate = useNavigate();

  const handleOnClick = useCallback(
    (id: string) => {
      navigate(`/codebooks/${id}`);
    },
    [navigate],
  );

  const { data, isLoading } = useQuery(
    ["codebookWarehouse", pageIndex, pageSize, search],
    () =>
      api.codebookWarehouse.getCodebookWarehouse(
        pageSize,
        pageIndex * pageSize,
        search,
      ),
    {
      keepPreviousData: true,
    },
  );

  // Calculate canNextPage based on data
  const canNextPage = data ? pageIndex < data.totalPages - 1 : false;

  if (isLoading) {
    return <Loader />;
  }

  return (
    <Box
      w="100%"
      h="full"
      pt="5.25rem"
      p="1rem"
      bg="white"
      display="flex"
      flexDirection="column"
      alignItems="center"
    >
      <HStack
        w="full"
        justify="space-between"
        pt="5.25rem"
        mb="1rem"
        flexWrap="wrap"
      >
        <Box fontSize={["lg", "xl", "2xl"]} fontWeight="semibold" color="black">
          ŠIFARNIK
        </Box>
        <ControlledInput
          name={"search"}
          // @ts-ignore
          w={["100%", "20rem"]}
          placeholder={"Pretraži"}
        />
      </HStack>
      <Stack w="full" gap="5">
        <Box overflowX="auto">
          <Table.Root
            size="sm"
            stickyHeader
            interactive
            variant="outline"
            minWidth="800px"
          >
            <Table.Header bg="gray.200">
              <Table.Row>
                <Table.ColumnHeader
                  key={1}
                  textAlign="center"
                  className="text-black font-semibold"
                >
                  ŠIFRA MATERIJALA
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  key={2}
                  textAlign="center"
                  className="text-black font-semibold"
                >
                  NAZIV MATERIJALA
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  key={3}
                  textAlign="center"
                  className="text-black font-semibold"
                >
                  TIP MATERIJALA
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  key={4}
                  textAlign="center"
                  className="text-black font-semibold"
                >
                  TIP PLOČE
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  key={4}
                  textAlign="center"
                  className="text-black font-semibold"
                ></Table.ColumnHeader>
                <Table.ColumnHeader
                  key={4}
                  textAlign="center"
                  className="text-black font-semibold"
                ></Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {data?.items?.map((item) => (
                <Table.Row
                  key={item.id}
                  className="hover:bg-gray-100 cursor-pointer"
                >
                  <Table.Cell className={"text-black"} textAlign={"center"}>
                    {item.code}
                  </Table.Cell>
                  <Table.Cell className={"text-black"} textAlign={"center"}>
                    {item.name}
                  </Table.Cell>
                  <Table.Cell className={"text-black"} textAlign={"center"}>
                    {materialTypeMapper(item.type as MaterialTypeEnum)}
                  </Table.Cell>
                  <Table.Cell className={"text-black"} textAlign={"center"}>
                    {plateTypeMapper(item.plateType)}
                  </Table.Cell>
                  <Table.Cell textAlign="center" color={"black"}>
                    <Button
                      size="sm"
                      variant="outline"
                      color={"#000000"}
                      backgroundColor={"#ffffff"}
                      borderColor={"#ffffff"}
                      fontWeight={"600"}
                      fontSize={"14px"}
                      borderRadius={"0.5rem"}
                      onClick={() => handleOnClick(item?.id)}
                    >
                      Pregled
                    </Button>
                  </Table.Cell>
                  <Table.Cell className={"text-black"} textAlign={"center"}>
                    <Dialog
                      id={item.id}
                      pageIndex={pageIndex}
                      pageSize={pageSize}
                    />
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Box>
        <CustomPagination
          count={data!.totalItems}
          pageSize={pageSize}
          currentPage={pageIndex}
          onPageChange={setPage}
          onNextPage={onNextPage}
          onPreviousPage={onPreviousPage}
          canNextPage={canNextPage}
          canPreviousPage={canPreviousPage}
          siblingCount={1}
        />
      </Stack>
    </Box>
  );
};

const Dialog = ({
  id,
  pageIndex,
  pageSize,
}: {
  id: string;
  pageIndex: number;
  pageSize: number;
}) => {
  const queryClient = useQueryClient();

  const { mutate } = useMutation({
    mutationFn: (id: string) =>
      api.codebookWarehouse.deleteCodebookWarehouse(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["codebookWarehouse", pageIndex, pageSize],
      });
      queryClient.invalidateQueries({
        queryKey: ["codebook"],
      });
    },
  });

  return (
    <DialogRoot role="alertdialog">
      <DialogTrigger asChild>
        <FaTrashAlt color={"black"} className={"cursor-pointer"} />
      </DialogTrigger>
      <DialogContent className={"bg-[#ffffff]"}>
        <DialogHeader>
          <DialogTitle className={"text-red-500 font-semibold text-[24px]"}>
            Upozorenje
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <p className={"text-black"}>
            Klikom na dugme "Potvrdi" obrisaćete podatke, ukoliko to ne želite
            kliknite na dugme "Odustani".
          </p>
        </DialogBody>
        <DialogFooter>
          <DialogActionTrigger asChild>
            <Button
              size={"sm"}
              colorPalette={"red"}
              width={"5rem"}
              color={"#000000"}
              backgroundColor={"#ffffff"}
              borderColor={"#ffffff"}
              fontWeight={"600"}
              fontSize={"14px"}
              borderRadius={"0.5rem"}
              variant="outline"
            >
              Odustani
            </Button>
          </DialogActionTrigger>
          <Button
            size={"sm"}
            colorPalette={"red"}
            width={"5rem"}
            color={"#EF4444"}
            backgroundColor={"#ffffff"}
            borderColor={"#ffffff"}
            fontWeight={"600"}
            fontSize={"14px"}
            borderRadius={"0.5rem"}
            variant={"outline"}
            onClick={() => mutate(id)}
          >
            Potvrdi
          </Button>
        </DialogFooter>
        <DialogCloseTrigger />
      </DialogContent>
    </DialogRoot>
  );
};

export const CodebookPage = () => {
  return (
    <ControlledForm schema={searchSchema}>
      <Codebook />
    </ControlledForm>
  );
};
